import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import SurveySubmissionHandler from '../components/surveys/SurveyComponents/SurveySubmissionHandler';
import DynamicSurveyRenderer from '../components/surveys/DynamicSurveyRenderer';
import { SurveyDefinition, DynamicSurveyResponse } from '../types/survey.types';
import { useSurveys } from '../hooks/useSurveys';
import { useResponses } from '../hooks/useResponses';
import { addBreadcrumb } from '../utils/sentry';

const SurveyRouter: React.FC = () => {
  const { surveyId, qrCode } = useParams<{ surveyId: string; qrCode: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<SurveyDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [responseId, setResponseId] = useState<string | null>(null);

  const { getSurvey } = useSurveys();
  const { submitResponse, loading: submitting } = useResponses();

  useEffect(() => {
    const loadSurvey = async () => {
      setLoading(true);
      setError(null);

      try {
        // Determine survey ID (from URL param or QR code)
        const targetSurveyId = qrCode || surveyId;

        if (!targetSurveyId) {
          setError('No survey specified');
          setLoading(false);
          return;
        }

        addBreadcrumb('Loading survey', 'survey', { surveyId: targetSurveyId, accessMethod: qrCode ? 'qr' : 'direct' });

        // Fetch survey from Firebase
        const surveyData = await getSurvey(targetSurveyId);

        if (!surveyData) {
          setError('Survey not found or no longer available');
          setLoading(false);
          return;
        }

        // Check if survey is active
        if (!surveyData.settings.isActive) {
          setError('This survey is currently inactive');
          setLoading(false);
          return;
        }

        // Check access permissions
        if (surveyData.settings.accessType === 'private') {
          setError('This survey requires special access permissions');
          setLoading(false);
          return;
        }

        // Check if survey has expired
        if (surveyData.settings.expiryDate) {
          const expiryDate = surveyData.settings.expiryDate.toDate?.() || new Date(surveyData.settings.expiryDate as any);

          if (new Date() > expiryDate) {
            setError('This survey has expired');
            setLoading(false);
            return;
          }
        }

        // Check if survey has reached max responses
        if (surveyData.settings.maxResponses && surveyData.analytics.totalResponses >= surveyData.settings.maxResponses) {
          setError('This survey has reached the maximum number of responses');
          setLoading(false);
          return;
        }

        setSurvey(surveyData);
        setLoading(false);
      } catch (err: any) {
        console.error('Error loading survey:', err);
        setError(err.message || 'Failed to load survey');
        setLoading(false);
      }
    };

    loadSurvey();
  }, [surveyId, qrCode, getSurvey]);

  /**
   * Handle survey completion
   */
  const handleSurveyComplete = async (response: DynamicSurveyResponse) => {
    try {
      addBreadcrumb('Submitting survey response', 'survey', {
        surveyId: response.surveyId,
        questionCount: Object.keys(response.responses).length,
      });

      const newResponseId = await submitResponse(response);

      setResponseId(newResponseId);
      setSubmissionSuccess(true);

      addBreadcrumb('Survey response submitted successfully', 'survey', { responseId: newResponseId });
    } catch (err: any) {
      console.error('Survey submission error:', err);
      alert(`Failed to submit survey: ${err.message}`);
    }
  };

  /**
   * Handle survey cancellation
   */
  const handleSurveyCancel = () => {
    if (window.confirm('Are you sure you want to leave? Your progress has been saved as a draft.')) {
      navigate('/');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading survey...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Survey Not Available
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="mt-6">
              <a
                href="/"
                className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors"
              >
                ← Return to Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Survey not found
  if (!survey) {
    return <Navigate to="/" replace />;
  }

  // Success state
  if (submissionSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Thank You!
            </h2>
            <p className="text-gray-600 mb-6">
              Your survey response has been submitted successfully.
            </p>
            {responseId && (
              <div className="bg-white rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-1">Reference ID:</p>
                <p className="font-mono text-sm text-gray-900">{responseId}</p>
              </div>
            )}
            
            <div className="space-y-4">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors"
              >
                ← Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle legacy surveys (cann-compounding uses old custom component)
  if (survey.id === 'cann-compounding' && survey.sections.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <SurveySubmissionHandler
          onSuccess={(id) => {
            setResponseId(id);
            setSubmissionSuccess(true);
          }}
          onError={(err) => {
            console.error('Survey submission error:', err);
            alert(`Failed to submit survey: ${err}`);
          }}
        />
      </div>
    );
  }

  // Render dynamic survey
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {submitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Submitting your response...</p>
          </div>
        </div>
      )}

      <DynamicSurveyRenderer
        surveyDefinition={survey}
        onComplete={handleSurveyComplete}
        onCancel={handleSurveyCancel}
      />
    </div>
  );
};

export default SurveyRouter;
